import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Paperclip, X, FileText, Image as ImageIcon, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { extractErrorMessage } from '../lib/errors';

function formatBytes(n) {
  if (!n) return '0 KB';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 102.4) / 10} KB`;
  return `${Math.round(n / (1024 * 102.4)) / 10} MB`;
}

function iconFor(mime, filename='') {
  const m = (mime || '').toLowerCase();
  const f = (filename || '').toLowerCase();
  if (m.startsWith('image/') || f.match(/\.(png|jpe?g|gif|webp)$/)) return ImageIcon;
  return FileText;
}

export default function AttachmentsPanel({ campaignId }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [max, setMax] = useState(3 * 1024 * 1024);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    if (!campaignId || campaignId === 'new') return;
    try {
      const r = await api.get(`/campaigns/${campaignId}/attachments`);
      setItems(r.data.attachments || []);
      setTotal(r.data.total_bytes || 0);
      setMax(r.data.max_total_bytes || 3 * 1024 * 1024);
    } catch (err) { console.warn('AttachmentsPanel load failed:', err); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (total + f.size > max) {
      toast.error(`Adding this would exceed the ${Math.round(max/(1024*1024))}MB Graph limit. Total used: ${formatBytes(total)}.`);
      e.target.value = '';
      return;
    }
    setUploading(true);
    const fd = new FormData(); fd.append('file', f);
    try {
      await api.post(`/campaigns/${campaignId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${f.name} attached`);
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Upload failed'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/campaigns/${campaignId}/attachments/${id}`); load(); toast.success('Attachment removed'); }
    catch { toast.error('Failed to remove'); }
  };

  if (!campaignId || campaignId === 'new') {
    return (
      <Card className="bg-[#111110] border-zinc-900 p-5">
        <div className="font-display text-lg flex items-center gap-2"><Paperclip size={16} className="text-[#d4a94a]"/>Attachments</div>
        <div className="text-sm text-zinc-500 mt-2">Save the campaign first to upload attachments.</div>
      </Card>
    );
  }

  const pctUsed = Math.min(100, Math.round((total / max) * 100));

  return (
    <Card className="bg-[#111110] border-zinc-900 p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="font-display text-lg flex items-center gap-2"><Paperclip size={16} className="text-[#d4a94a]"/>Attachments</div>
          <div className="text-xs text-zinc-500 mt-1">Sent as real Outlook file attachments on every email in this campaign. PDF, DOCX, PNG, JPG. Total limit {formatBytes(max)} (Microsoft Graph sendMail cap).</div>
        </div>
        <label className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-zinc-800 hover:border-[#3a2f18] text-sm text-zinc-200 hover:text-[#f5d67a] cursor-pointer ${uploading?'opacity-50 pointer-events-none':''}`}>
          <Upload size={14}/>{uploading ? 'Uploading…' : 'Upload file'}
          <input type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg,.txt" onChange={onFile} className="hidden"/>
        </label>
      </div>

      {items.length === 0 ? (
        <div className="mt-4 border border-dashed border-zinc-800 rounded-lg p-6 text-center text-sm text-zinc-500">
          No attachments yet. Upload a resume, cover letter or portfolio.
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {items.map(a => {
            const Icon = iconFor(a.mime, a.filename);
            return (
              <div key={a.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-zinc-900 bg-[#0f0e0d]">
                <div className="w-9 h-9 rounded bg-[#1a1815] border border-[#3a2f18] flex items-center justify-center text-[#f5d67a]"><Icon size={16}/></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-zinc-100 truncate">{a.filename}</div>
                  <div className="text-xs text-zinc-500">{formatBytes(a.size)}</div>
                </div>
                <button onClick={()=>remove(a.id)} className="text-zinc-500 hover:text-red-400"><X size={14}/></button>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-4">
        <div className="flex justify-between text-[10px] text-zinc-500 mb-1"><span>Size used</span><span>{formatBytes(total)} / {formatBytes(max)}</span></div>
        <div className="h-1 bg-zinc-900 rounded overflow-hidden">
          <div className={`h-full ${pctUsed > 85 ? 'bg-red-500' : 'bg-[#d4a94a]'}`} style={{width: `${pctUsed}%`}}/>
        </div>
      </div>
    </Card>
  );
}
